/**
 * Procedure Catalog Constants
 *
 * Shared enums + metadata for the Procedure Catalog and Doctor Rate Overrides
 * admin screens. Backend validates `category` and `gst_rate` against these
 * same allowed values — keep them in sync.
 */

// ── Category enum ────────────────────────────────────────────────────
export const PROCEDURE_CATEGORIES = [
  { value: 'dental',  label: 'Dental' },
  { value: 'gp',      label: 'General Practice' },
  { value: 'derm',    label: 'Dermatology' },
  { value: 'cardio',  label: 'Cardiology' },
  { value: 'ortho',   label: 'Orthopedics' },
  { value: 'ent',     label: 'ENT' },
];

// ── Lab isolation ────────────────────────────────────────────────────
// Lab tests share the catalog table (category 'lab') but must NEVER appear in
// procedure surfaces — the admin Procedure Catalog, the add-to-visit picker,
// quick-add, registration, appointment booking. They have their own Lab
// Catalog + advise flow. Every procedure surface filters the catalog through
// this so a lab test can't leak in via a shared flag (e.g. receptionist_allowed).
export const LAB_CATEGORY = 'lab';
export const excludeLabTests = (list) =>
  (list || []).filter((p) => p && p.category !== LAB_CATEGORY);

export const PROCEDURE_CATEGORY_LABELS = PROCEDURE_CATEGORIES.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
  {}
);

// ── Short pill-tab labels (keeps filter bar compact) ─────────────────
export const PROCEDURE_CATEGORY_SHORT_LABELS = {
  dental: 'Dental',
  gp: 'GP',
  derm: 'Derm',
  cardio: 'Cardio',
  ortho: 'Ortho',
  ent: 'ENT',
};

// ── Department → Clinical type smart-default (A4 create-form only) ──
// Case-insensitive match of a department NAME to a known category value.
// Only covers clean speciality-name matches; anything else (e.g.
// "Emergency", "Camp", "Gynaecology") intentionally has no entry so the
// caller leaves Clinical type for the user to pick.
const DEPT_NAME_TO_CATEGORY = {
  dental: 'dental',
  dentistry: 'dental',
  gp: 'gp',
  'general practice': 'gp',
  'general medicine': 'gp',
  dermatology: 'derm',
  derm: 'derm',
  cardiology: 'cardio',
  cardio: 'cardio',
  orthopedics: 'ortho',
  orthopaedics: 'ortho',
  ortho: 'ortho',
  ent: 'ent',
  'ear nose throat': 'ent',
};

export function categoryFromDeptName(name) {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  return DEPT_NAME_TO_CATEGORY[key] || null;
}

// ── GST ──────────────────────────────────────────────────────────────
export const GST_RATES = [0, 5, 12, 18, 28];

// ── Flag chip metadata (rendered in the list row) ────────────────────
export const PROCEDURE_FLAGS = [
  { key: 'is_tooth_specific',    label: 'Tooth',        color: 'blue'  },
  { key: 'nurse_allowed',        label: 'Nurse',        color: 'green' },
  { key: 'receptionist_allowed', label: 'Receptionist', color: 'amber' },
  { key: 'skip_payment',         label: 'No charge',    color: 'slate' },
  { key: 'one_time_only',        label: 'One-time only', color: 'violet' },
];

// Hex mapping for chip backgrounds / text. Uses the same palette the rest
// of the app relies on (green=#22c55e, amber=#eab308, blue=#3b82f6).
export const PROCEDURE_FLAG_COLORS = {
  blue:  { bg: 'rgba(59, 130, 246, 0.15)',  text: '#3b82f6',  border: 'rgba(59, 130, 246, 0.3)'  },
  green: { bg: 'rgba(34, 197, 94, 0.15)',   text: '#16a34a',  border: 'rgba(34, 197, 94, 0.3)'   },
  amber: { bg: 'rgba(234, 179, 8, 0.15)',   text: '#ca8a04',  border: 'rgba(234, 179, 8, 0.3)'   },
  slate: { bg: 'rgba(100, 116, 139, 0.15)', text: '#475569',  border: 'rgba(100, 116, 139, 0.3)' },
  violet:{ bg: 'rgba(139, 92, 246, 0.15)',  text: '#7c3aed',  border: 'rgba(139, 92, 246, 0.3)'  },
};

// ── Plain-language (i) hint copy ─────────────────────────────────────
// Shown to clinic staff in tooltips next to each flag. No technical terms.
export const PROCEDURE_FLAG_HINTS = {
  is_tooth_specific:
    "When you add this, you'll be asked to pick which tooth or teeth it's "
    + 'for. Use it for work done on specific teeth — like a filling, root '
    + 'canal, or crown.',
  nurse_allowed:
    'A nurse can start this procedure for a patient on their own, without '
    + 'waiting for a doctor to add it first.',
  receptionist_allowed:
    'The front desk can start this procedure for a patient on their own, '
    + 'without waiting for a doctor to add it first.',
  skip_payment:
    'No payment is taken for this procedure. It goes straight to whoever '
    + 'will do it, without stopping at the front desk to pay. Its price is '
    + 'kept for your records but nothing is charged.',
  one_time_only:
    'This item must be done once — the doctor cannot make it a repeating '
    + '“no-expiry” standing order. Turn this on for things that should never '
    + 'repeat, like a pregnancy dating scan. (Most items leave this off.)',
};
